import React, { FC, useCallback, useMemo, useEffect, useContext, useRef } from "react";
import { useRequestInit } from "adviesbox-shared";
import { useFetchData } from "../../../shared/hooks/usefetchdata";
import LastenOverzichtTable from "../lastenoverzicht-table/lastenoverzicht-table";
import { LastenOverzicht } from "../../../.generated/rapportage/rapportagetypes";
import { AppDataContext } from "../../../navigation/appdata-context";

interface LastenOverzichtFetcherProps {
  voorstelId?: string;
  beperkteLastenTonen: boolean;
  enabled?: boolean;
  onDataLoaded?: (data: LastenOverzicht) => void;
  jarenTonen?: boolean;
}

export const LastenOverzichtFetcher: FC<LastenOverzichtFetcherProps> = ({
  voorstelId: initialVoorstelId,
  beperkteLastenTonen,
  enabled = true,
  onDataLoaded,
  jarenTonen
}) => {
  const { setBerekenId } = useContext(AppDataContext);
  const { settings, params, requestInit } = useRequestInit<{
    vestiging: string;
    adviesdossier: string;
    voorstel: string;
  }>();

  const voorstelId = initialVoorstelId === "999" ? undefined : initialVoorstelId;

  const getLastenOverzichtUrl = useMemo(() => {
    const baseUrl = settings.rapportageOrigin;
    return voorstelId
      ? `${baseUrl}/Voorstellen/${voorstelId}/SnelInzicht/LastenOverzicht`
      : `${baseUrl}/Adviesdossiers/${params.adviesdossier}/SnelInzicht/LastenOverzicht`;
  }, [settings.rapportageOrigin, params.adviesdossier, voorstelId]);

  const getRequestInit = useCallback(
    () => ({
      ...requestInit,
      method: "POST",
      url: getLastenOverzichtUrl
    }),
    [requestInit, getLastenOverzichtUrl]
  );

  const { data, loading, error } = useFetchData<LastenOverzicht>(
    getLastenOverzichtUrl,
    getRequestInit,
    enabled
  );

  const dataProcessedRef = useRef<LastenOverzicht | null>(null);

  useEffect(() => {
    if (data && onDataLoaded && data !== dataProcessedRef.current) {
      onDataLoaded(data);
      // ref bijhouden, anders wordt deze de call iedere render aangeroepen
      dataProcessedRef.current = data;

      // berekenId resetten
      setBerekenId(null);
    }
  }, [data, onDataLoaded, setBerekenId]);

  if (error) {
    return (
      <div role="alert" className="error-message">
        Er is een fout opgetreden: {String(error)}
      </div>
    );
  }

  return (
    <LastenOverzichtTable
      data={data}
      beperkteLastenTonen={beperkteLastenTonen}
      jarenTonen={jarenTonen}
      loading={loading}
    />
  );
};
